import { useState, useMemo, useCallback, useEffect, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import {
  qCalendarSettings,
  mapCalendarSettingsToUI,
  mapUIToCalendarSettingsColumns,
  type CalendarSettingsRow,
} from '@helios/data-access';
import {
  CALENDAR_SETTINGS_DEFAULTS,
  type CalendarDefaultView,
  type WeekStartDay,
  type ColorAppointmentsBy,
} from '@helios/fixtures';

// ─── UI Form State ───

interface CalendarSettings {
  categoryFilter: boolean;
  statusFilter: boolean;
  providerFilter: boolean;
  serviceFilter: boolean;
  roomFilter: boolean;
  locationFilter: boolean;
  defaultView: CalendarDefaultView;
  weekStartsOn: WeekStartDay;
  workingHoursStartMin: number;
  workingHoursEndMin: number;
  slotIntervalMin: number;
  colorBy: ColorAppointmentsBy;
}

/** Fixture defaults mapped to UI shape */
const DEFAULTS: CalendarSettings = {
  categoryFilter: CALENDAR_SETTINGS_DEFAULTS.filter_category_enabled,
  statusFilter: CALENDAR_SETTINGS_DEFAULTS.filter_status_enabled,
  providerFilter: CALENDAR_SETTINGS_DEFAULTS.filter_provider_enabled,
  serviceFilter: CALENDAR_SETTINGS_DEFAULTS.filter_service_enabled,
  roomFilter: CALENDAR_SETTINGS_DEFAULTS.filter_room_enabled,
  locationFilter: CALENDAR_SETTINGS_DEFAULTS.filter_location_enabled,
  defaultView: CALENDAR_SETTINGS_DEFAULTS.default_view,
  weekStartsOn: CALENDAR_SETTINGS_DEFAULTS.week_starts_on,
  workingHoursStartMin: CALENDAR_SETTINGS_DEFAULTS.working_hours_start_min,
  workingHoursEndMin: CALENDAR_SETTINGS_DEFAULTS.working_hours_end_min,
  slotIntervalMin: CALENDAR_SETTINGS_DEFAULTS.slot_interval_min,
  colorBy: CALENDAR_SETTINGS_DEFAULTS.color_appointments_by,
};

// ─── Time Helpers ───

function minutesToTimeLabel(mins: number): string {
  const h24 = Math.floor(mins / 60);
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${String(h12).padStart(2, '0')}:00 ${ampm}`;
}

function timeLabelToMinutes(label: string): number {
  const match = label.match(/^(\d{2}):00\s(AM|PM)$/);
  if (!match) return 480;
  let h = parseInt(match[1], 10);
  if (match[2] === 'PM' && h !== 12) h += 12;
  if (match[2] === 'AM' && h === 12) h = 0;
  return h * 60;
}

const HOUR_OPTIONS: string[] = [];
for (let h = 6; h <= 22; h++) {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  HOUR_OPTIONS.push(`${String(h12).padStart(2, '0')}:00 ${ampm}`);
}

// ─── Enum Display Helpers ───

const WEEK_START_OPTIONS: { value: WeekStartDay; label: string }[] = [
  { value: 'sun', label: 'Sunday' },
  { value: 'mon', label: 'Monday' },
  { value: 'sat', label: 'Saturday' },
];

const SLOT_OPTIONS: { value: number; label: string }[] = [
  { value: 15, label: '15 minutes' },
  { value: 20, label: '20 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '60 minutes' },
];

const COLOR_OPTIONS: { value: ColorAppointmentsBy; label: string }[] = [
  { value: 'category', label: 'Category' },
  { value: 'provider', label: 'Provider' },
  { value: 'status', label: 'Status' },
];

// ─── Toggle ───

function Toggle({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Card ───

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-6)',
      }}
    >
      {children}
    </div>
  );
}

// ─── Setting Row (toggle) ───

function SettingToggleRow({
  title,
  description,
  checked,
  onChange,
  divider = true,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (val: boolean) => void;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          {description}
        </div>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

// ─── Setting Row (select with value/label) ───

function SettingSelectRow<T extends string | number>({
  title,
  description,
  value,
  options,
  onChange,
  divider = true,
}: {
  title: string;
  description: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (val: T) => void;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          {description}
        </div>
      </div>
      <select
        value={String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          const parsed = typeof value === 'number' ? (Number(raw) as T) : (raw as T);
          onChange(parsed);
        }}
        style={selectStyle}
      >
        {options.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Setting Row (working hours with two selects) ───

function WorkingHoursRow({
  title,
  description,
  startValue,
  endValue,
  options,
  onStartChange,
  onEndChange,
  divider = true,
}: {
  title: string;
  description: string;
  startValue: string;
  endValue: string;
  options: string[];
  onStartChange: (val: string) => void;
  onEndChange: (val: string) => void;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          {description}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <select
          value={startValue}
          onChange={(e) => onStartChange(e.target.value)}
          style={selectStyle}
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>–</span>
        <select value={endValue} onChange={(e) => onEndChange(e.target.value)} style={selectStyle}>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── Radio group row ───

function SettingRadioRow<T extends string>({
  title,
  description,
  value,
  options,
  onChange,
  divider = true,
}: {
  title: string;
  description: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (val: T) => void;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          {description}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          alignItems: 'flex-end',
        }}
      >
        {options.map((opt) => (
          <label
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              color: value === opt.value ? 'var(--color-primary)' : 'var(--text)',
              fontWeight:
                value === opt.value ? 'var(--font-weight-semi)' : 'var(--font-weight-normal)',
            }}
          >
            {opt.label}
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 'var(--radius-full)',
                border:
                  value === opt.value
                    ? '5px solid var(--color-primary)'
                    : '2px solid var(--border)',
                background: '#ffffff',
                flexShrink: 0,
                transition: 'border 150ms ease',
              }}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Calendar Settings Page ───

export function CalendarSettingsPage() {
  const { db } = usePowerSyncDb();
  const { data: rows = [] } = usePowerSyncQuery<CalendarSettingsRow>(qCalendarSettings());

  const [settings, setSettings] = useState<CalendarSettings>({ ...DEFAULTS });
  const [savedSnapshot, setSavedSnapshot] = useState<CalendarSettings>({ ...DEFAULTS });
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Populate form from DB row once data arrives
  useEffect(() => {
    if (loaded || rows.length === 0) return;
    const row = rows[0];
    const ui = mapCalendarSettingsToUI(row);
    const mapped: CalendarSettings = {
      categoryFilter: ui.categoryFilter,
      statusFilter: ui.statusFilter,
      providerFilter: ui.providerFilter,
      serviceFilter: ui.serviceFilter,
      roomFilter: ui.roomFilter,
      locationFilter: ui.locationFilter,
      defaultView: ui.defaultView,
      weekStartsOn: ui.weekStartsOn,
      workingHoursStartMin: ui.workingHoursStartMin,
      workingHoursEndMin: ui.workingHoursEndMin,
      slotIntervalMin: ui.slotIntervalMin,
      colorBy: ui.colorBy,
    };
    setSettings(mapped);
    setSavedSnapshot(mapped);
    setLoaded(true);
  }, [rows, loaded]);

  const isDirty = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(savedSnapshot);
  }, [settings, savedSnapshot]);

  const update = <K extends keyof CalendarSettings>(key: K, value: CalendarSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const handleSave = useCallback(async () => {
    if (!db || saving) return;
    setSaving(true);
    try {
      const cols = mapUIToCalendarSettingsColumns(settings);
      if (rows.length > 0) {
        // Update existing row
        const id = rows[0].id;
        await db.execute(
          `UPDATE appointment_calendar_settings SET
            filter_category_enabled = ?,
            filter_status_enabled = ?,
            filter_provider_enabled = ?,
            filter_service_enabled = ?,
            filter_room_enabled = ?,
            filter_location_enabled = ?,
            default_view = ?,
            week_starts_on = ?,
            working_hours_start_min = ?,
            working_hours_end_min = ?,
            slot_interval_min = ?,
            color_appointments_by = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            cols.filter_category_enabled,
            cols.filter_status_enabled,
            cols.filter_provider_enabled,
            cols.filter_service_enabled,
            cols.filter_room_enabled,
            cols.filter_location_enabled,
            cols.default_view,
            cols.week_starts_on,
            cols.working_hours_start_min,
            cols.working_hours_end_min,
            cols.slot_interval_min,
            cols.color_appointments_by,
            cols.updated_at,
            id,
          ],
        );
      }
      setSavedSnapshot({ ...settings });
      showToast('Settings saved');
    } catch (err) {
      console.error('[CalendarSettings] save error:', err);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [db, saving, settings, rows, showToast]);

  const handleReset = useCallback(async () => {
    if (!db) return;
    const resetValues = { ...DEFAULTS };
    setSettings(resetValues);

    try {
      const cols = mapUIToCalendarSettingsColumns(resetValues);
      if (rows.length > 0) {
        const id = rows[0].id;
        await db.execute(
          `UPDATE appointment_calendar_settings SET
            filter_category_enabled = ?,
            filter_status_enabled = ?,
            filter_provider_enabled = ?,
            filter_service_enabled = ?,
            filter_room_enabled = ?,
            filter_location_enabled = ?,
            default_view = ?,
            week_starts_on = ?,
            working_hours_start_min = ?,
            working_hours_end_min = ?,
            slot_interval_min = ?,
            color_appointments_by = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            cols.filter_category_enabled,
            cols.filter_status_enabled,
            cols.filter_provider_enabled,
            cols.filter_service_enabled,
            cols.filter_room_enabled,
            cols.filter_location_enabled,
            cols.default_view,
            cols.week_starts_on,
            cols.working_hours_start_min,
            cols.working_hours_end_min,
            cols.slot_interval_min,
            cols.color_appointments_by,
            cols.updated_at,
            id,
          ],
        );
      }
      setSavedSnapshot(resetValues);
      showToast('Reset to defaults');
    } catch (err) {
      console.error('[CalendarSettings] reset error:', err);
      showToast('Failed to reset');
    }
  }, [db, rows, showToast]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-xl2)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl2)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
              }}
            >
              Calendar Settings
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 22,
                  padding: '0 var(--sp-2)',
                  borderRadius: 'var(--radius-xs)',
                  background: '#fef3c7',
                  color: '#92400e',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                Admin only
              </span>
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Configure scheduling views and filters
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            type="button"
            onClick={handleReset}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-2) var(--sp-3)',
            }}
          >
            <NavIcon name="refresh-cw" />
            Reset to Default
          </button>
          <Button variant="gradient" size="md" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-6)' }}>
        {/* Toast */}
        {toast && (
          <div
            style={{
              marginBottom: 'var(--sp-4)',
              padding: 'var(--sp-3) var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: '#dcfce7',
              color: '#15803d',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              maxWidth: 620,
            }}
          >
            {toast}
          </div>
        )}

        <div
          style={{ maxWidth: 620, display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}
        >
          {/* ── Card 1: Filters ── */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Filters</h2>
              <p style={cardDescStyle}>
                Choose which filters are available across Calendar and List view
              </p>
            </div>

            <SettingToggleRow
              title="Category filter"
              description="Allow filtering appointments by category"
              checked={settings.categoryFilter}
              onChange={(v) => update('categoryFilter', v)}
            />
            <SettingToggleRow
              title="Status filter"
              description="Allow filtering appointments by appointment status"
              checked={settings.statusFilter}
              onChange={(v) => update('statusFilter', v)}
            />
            <SettingToggleRow
              title="Provider filter"
              description="Allow filtering appointments by provider"
              checked={settings.providerFilter}
              onChange={(v) => update('providerFilter', v)}
            />
            <SettingToggleRow
              title="Service filter"
              description="Allow filtering appointments by service"
              checked={settings.serviceFilter}
              onChange={(v) => update('serviceFilter', v)}
            />
            <SettingToggleRow
              title="Room filter"
              description="Allow filtering appointments by room (optional)"
              checked={settings.roomFilter}
              onChange={(v) => update('roomFilter', v)}
            />
            <SettingToggleRow
              title="Location filter"
              description="Allow filtering appointments by clinic/location (multi-location setups)"
              checked={settings.locationFilter}
              onChange={(v) => update('locationFilter', v)}
              divider={false}
            />
          </Card>

          {/* ── Card 2: Views ── */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Views</h2>
              <p style={cardDescStyle}>Default calendar view and time grid preferences</p>
            </div>

            <SettingSelectRow
              title="Week starts on"
              description="First day of the week in calendar"
              value={settings.weekStartsOn}
              options={WEEK_START_OPTIONS}
              onChange={(v) => update('weekStartsOn', v as WeekStartDay)}
            />
            <WorkingHoursRow
              title="Working hours"
              description="Operating hours for appointments"
              startValue={minutesToTimeLabel(settings.workingHoursStartMin)}
              endValue={minutesToTimeLabel(settings.workingHoursEndMin)}
              options={HOUR_OPTIONS}
              onStartChange={(v) => update('workingHoursStartMin', timeLabelToMinutes(v))}
              onEndChange={(v) => update('workingHoursEndMin', timeLabelToMinutes(v))}
            />
            <SettingSelectRow
              title="Slot interval"
              description="Time slot duration for appointments"
              value={settings.slotIntervalMin}
              options={SLOT_OPTIONS}
              onChange={(v) => update('slotIntervalMin', v)}
              divider={false}
            />
          </Card>

          {/* ── Card 3: Display ── */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Display</h2>
              <p style={cardDescStyle}>Customize how appointments appear in the calendar</p>
            </div>

            <SettingRadioRow
              title="Color appointments by"
              description="Choose the color coding scheme"
              value={settings.colorBy}
              options={COLOR_OPTIONS}
              onChange={(v) => update('colorBy', v as ColorAppointmentsBy)}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Shared styles ───

const cardTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-lg)',
  color: 'var(--text)',
  margin: 0,
};

const cardDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
  margin: '2px 0 0',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  minWidth: 130,
};
